// src/prototype/components/sep/SEPSectionExecutionRecommendation.tsx
// Component 10: approval_checklist

import React from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import type { MappedApprovalChecklist, MappedRecommendedNextStep } from '../../utils/mapStrategicExecutionPlan';
import { formatRoiMillions } from '../../utils/mapStrategicExecutionPlan';

export interface SEPSectionExecutionRecommendationProps {
  clientName: string;
  approvalChecklist: MappedApprovalChecklist | null;
}

const NextStep: React.FC<{ step: MappedRecommendedNextStep }> = ({ step }) => (
  <div className="flex items-start gap-3">
    <div className="w-7 h-7 rounded-full bg-[#FF6E4C] flex items-center justify-center shrink-0 mt-0.5">
      <span className="text-[11px] font-bold text-white">{step.step}</span>
    </div>
    <div className="flex-1 border border-[#e5e7eb] rounded-xl p-3.5 bg-white">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-[#18365E]">{step.title}</p>
        <span className="text-[10px] font-semibold text-[#717182] bg-[#F5F7FA] px-2 py-0.5 rounded-full border border-[#e5e7eb] shrink-0 whitespace-nowrap">
          {step.timeline}
        </span>
      </div>
      <p className="text-xs text-[#717182] leading-relaxed mt-1">{step.description}</p>
      {step.pilotInitiatives.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {step.pilotInitiatives.map(init => (
            <span key={init.id} className="text-[10px] px-2 py-0.5 bg-[#18365E]/5 border border-[#18365E]/10 rounded-full text-[#18365E]">
              {init.name}
            </span>
          ))}
        </div>
      )}
    </div>
  </div>
);

export const SEPSectionExecutionRecommendation: React.FC<SEPSectionExecutionRecommendationProps> = ({
  clientName, approvalChecklist,
}) => {
  if (!approvalChecklist) {
    return (
      <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
        <p className="text-sm text-[#717182]">Execution recommendation data is not available.</p>
      </div>
    );
  }

  const {
    portfolioRoiLow,
    portfolioRoiHigh,
    portfolioRoiDisplay,
    executionHorizon,
    initiativesSelectedCount,
    quickWinInitiative,
    advisorySummary,
    recommendedNextSteps,
  } = approvalChecklist;

  const roiDisplay = portfolioRoiDisplay || formatRoiMillions(portfolioRoiLow, portfolioRoiHigh);

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 10</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Execution Recommendation</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Strategic advisory conclusion and recommended next steps</p>
        </div>
      </div>

      <div className="bg-[#18365E] rounded-xl p-5 mb-6 text-white">
        <p className="text-base font-bold mb-3 leading-snug">
          {clientName} is positioned to execute a high-confidence portfolio that delivers{' '}
          <span className="text-[#FF6E4C]">{roiDisplay}</span> in annual value over a{' '}
          <span className="text-[#FF6E4C]">{executionHorizon}</span> horizon.
        </p>
        <p className="text-sm text-white/75 leading-relaxed">
          The selected {initiativesSelectedCount} initiative{initiativesSelectedCount !== 1 ? 's' : ''} represent the optimal intersection of strategic fit, ROI confidence, and organizational readiness.
          {quickWinInitiative && (
            <> Quick Win initiative <strong className="text-white">{quickWinInitiative.name}</strong> should be activated immediately to generate early momentum and executive confidence.</>
          )}
        </p>
        <p className="text-sm text-white/75 leading-relaxed mt-2">
          This plan provides the documentation, sequencing, resource model, and governance framework required for board-level approval and sustainable delivery.
        </p>
      </div>

      <div className="mb-6">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Advisory Summary</p>
        {advisorySummary.length > 0 ? (
          <div className="space-y-2.5 bg-[#F5F7FA] rounded-xl p-4 border border-[#e5e7eb]">
            {advisorySummary.map((text, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-[#FF6E4C] shrink-0 mt-px" />
                <p className="text-sm text-[#18365E]/85 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#717182]">Advisory summary is not available.</p>
        )}
      </div>

      <div>
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Recommended Next Steps</p>
        {recommendedNextSteps.length > 0 ? (
          <div className="space-y-3">
            {recommendedNextSteps.map(step => (
              <NextStep key={step.step} step={step} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#717182]">Recommended next steps are not available.</p>
        )}
      </div>

      <div className="mt-6 pt-5 border-t border-[#e5e7eb] flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-[#18365E]">Strategic Execution Plan</p>
          <p className="text-[10px] text-[#717182]">{clientName} · Prepared by DVA · Adaptive Product</p>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-semibold text-[#FF6E4C]/80">
          <span>Proceed to Command Center</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </div>
  );
};

export default SEPSectionExecutionRecommendation;
